import { useEffect } from "react";
import ReactDOM from "react-dom";
import { Navigation, A11y } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";

import placeholderImg from "../../images/photo-placeholder.svg";
import { TProfile } from "../../types";
import styles from "./profile-preview-modal.module.css";

type PreviewProfile = TProfile & {
    approved_photos?: TProfile["photos"];
};

type Props = {
    profile: PreviewProfile | null;
    isOpen: boolean;
    onClose: () => void;
};

const getProfilePhotoList = (profile?: PreviewProfile | null) => {
    return profile?.photos ?? profile?.approved_photos ?? [];
};

const getProfilePhotos = (profile: PreviewProfile) => {
    const photos = getProfilePhotoList(profile)
        .map((photo) => {
            const baseUrl = photo.image_urls?.profile || photo.image_urls?.middle || photo.url || "";
            if (!baseUrl) return null;

            return photo.updated_at
                ? `${baseUrl}${baseUrl.includes("?") ? "&" : "?"}v=${encodeURIComponent(photo.updated_at)}`
                : baseUrl;
        })
        .filter((url): url is string => Boolean(url));

    return photos.length > 0 ? photos : [placeholderImg];
};

const formatEventCountWord = (count: number) => {
    const mod100 = count % 100;
    const mod10 = count % 10;

    if (mod100 >= 11 && mod100 <= 14) return "событий";
    if (mod10 === 1) return "событие";
    if (mod10 >= 2 && mod10 <= 4) return "события";

    return "событий";
};

const getMemberStatusText = (profile: PreviewProfile) => {
    if (!profile.member_status_label) return null;

    const count = profile.member_status_events_count ?? profile.events_visit ?? 0;

    return {
        label: profile.member_status_label,
        countText: `${count} ${formatEventCountWord(count)}`,
    };
};

const ProfilePreviewModal = ({ profile, isOpen, onClose }: Props) => {
    useEffect(() => {
        if (!isOpen) return;

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                onClose();
            }
        };

        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [isOpen, onClose]);

    if (!isOpen || !profile) {
        return null;
    }

    const photos = getProfilePhotos(profile);
    const goals = profile.goals ?? [];
    const interests = profile.interests ?? [];
    const lifestyles = profile.lifestyles ?? [];
    const heading = profile.hide_year || profile.age === undefined
        ? profile.name
        : `${profile.name}, ${profile.age}`;
    const relationshipText = !profile.hide_relationship_status && profile.relationship_status_str
        ? profile.relationship_status_str
        : "Не указано";
    const memberStatusText = getMemberStatusText(profile);

    const modal = (
        <div className={styles.modalRoot}>
            <button
                type="button"
                className={styles.modalBackdrop}
                onClick={onClose}
                aria-label="Закрыть анкету"
            />

            <div className={styles.profileModal} role="dialog" aria-modal="true" aria-label={`Анкета ${profile.name}`}>
                <button type="button" className={styles.modalClose} onClick={onClose} aria-label="Закрыть">
                    <svg width="18" height="18" viewBox="0 0 22 22" fill="none">
                        <path d="M1.5 20.5L20.5 1.5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                        <path d="M1.5 1.5L20.5 20.5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                    </svg>
                </button>

                <div className={styles.modalPhoto}>
                    <Swiper
                        modules={[Navigation, A11y]}
                        spaceBetween={0}
                        slidesPerView={1}
                        navigation={photos.length > 1}
                        className={styles.modalSwiper}
                    >
                        {photos.map((photoUrl, index) => (
                            <SwiperSlide key={`${photoUrl}-${index}`}>
                                <div className={styles.modalSlide}>
                                    <img src={photoUrl} alt="" />
                                </div>
                            </SwiperSlide>
                        ))}
                    </Swiper>

                </div>

                <div className={styles.modalContent}>
                    <div className={styles.divider}>
                        <div className={styles.detailsHeading}>{heading}</div>
                        {memberStatusText && (
                            <div className={styles.memberStatus}>
                                <span>Статус: </span>
                                <span className={styles.memberStatusLabel}>{memberStatusText.label}</span>
                                <span> ({memberStatusText.countText})</span>
                            </div>
                        )}

                        {!profile.hide_relationship_status && (
                            <div className={styles.infoBlockRelationship}>
                                <svg width="12" height="14" viewBox="0 0 12 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M2.125 3.54167C2.125 1.58566 3.71066 0 5.66667 0C7.62267 0 9.20833 1.58566 9.20833 3.54167C9.20833 5.49767 7.62267 7.08333 5.66667 7.08333C3.71066 7.08333 2.125 5.49767 2.125 3.54167ZM5.66667 5.66667C6.84027 5.66667 7.79167 4.71527 7.79167 3.54167C7.79167 2.36806 6.84027 1.41667 5.66667 1.41667C4.49306 1.41667 3.54167 2.36806 3.54167 3.54167C3.54167 4.71527 4.49306 5.66667 5.66667 5.66667Z" fill="black" fillOpacity="0.5" />
                                    <path d="M1.65973 9.45139C0.597022 10.5141 0 11.9554 0 13.4583H1.41667C1.41667 12.3312 1.86443 11.2502 2.66146 10.4531C3.45849 9.6561 4.5395 9.20833 5.66667 9.20833C6.79384 9.20833 7.87484 9.6561 8.67187 10.4531C9.4689 11.2502 9.91667 12.3312 9.91667 13.4583H11.3333C11.3333 11.9554 10.7363 10.5141 9.67361 9.45139C8.6109 8.38869 7.16956 7.79167 5.66667 7.79167C4.16377 7.79167 2.72243 8.38869 1.65973 9.45139Z" fill="black" fillOpacity="0.5" />
                                </svg>
                                {relationshipText}
                            </div>
                        )}
                    </div>

                    <div className={styles.modalSection}>
                        <div className={styles.modalTitle}>В поиске</div>
                        {goals.length > 0 ? (
                            <div className={styles.modalItems}>
                                {goals.map((goal) => (
                                    <span key={goal.id} className={styles.modalItem}>
                                        {goal.title}
                                    </span>
                                ))}
                            </div>
                        ) : (
                            <div className={styles.modalEmpty}>Не указано</div>
                        )}
                    </div>

                    <div className={styles.modalSection}>
                        <div className={styles.modalTitle}>Интересы</div>
                        {interests.length > 0 ? (
                            <div className={styles.modalItems}>
                                {interests.map((interest) => (
                                    <span key={interest.id} className={styles.modalItem}>
                                        {interest.title}
                                    </span>
                                ))}
                            </div>
                        ) : (
                            <div className={styles.modalEmpty}>Не указано</div>
                        )}
                    </div>

                    <div className={styles.modalSection}>
                        <div className={styles.modalTitle}>Образ жизни</div>
                        {lifestyles.length > 0 ? (
                            <div className={styles.modalItems}>
                                {lifestyles.map((lifestyle) => (
                                    <span key={lifestyle.id} className={styles.modalItem}>
                                        {lifestyle.title}
                                    </span>
                                ))}
                            </div>
                        ) : (
                            <div className={styles.modalEmpty}>Не указано</div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );

    const modalRoot = typeof document !== "undefined" ? document.getElementById("react-modals") : null;

    return modalRoot ? ReactDOM.createPortal(modal, modalRoot) : modal;
};

export default ProfilePreviewModal;
